import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { FilterDatePicker } from '../ui/date-picker';
import { PlusCircle, Edit, Trash2, CheckCircle, XCircle, AlertTriangle, Shield, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useMMDLocations } from '../../hooks/useMMDLocations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SurveyorManagement = ({ onRefresh }) => {
  const [surveyors, setSurveyors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentSurveyor, setCurrentSurveyor] = useState(null);
  const [formData, setFormData] = useState({ 
    name: '', 
    type: 'internal', 
    department: 'Engine',
    grades: [],
    mmd_locations: [],
    is_active: true,
    inactive_reason: '',
    transferred_to_mmd: '',
    inactive_date: ''
  });
  
  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletePreview, setDeletePreview] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [surveyorToDelete, setSurveyorToDelete] = useState(null);

  const engineGrades = ['MEO Class 1', 'MEO Class 2', 'MEO Class 4', 'ETO'];
  const deckGrades = ['Master', 'Chief Mate', '2MFG'];
  
  // Fetch MMD locations from API (with full names like "Kolkata MMD")
  const { locations: mmdLocations } = useMMDLocations(false, true);
  
  const availableGrades = formData.department === 'Engine' ? engineGrades : deckGrades;

  useEffect(() => {
    fetchSurveyors();
  }, []);

  const fetchSurveyors = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/surveyors`);
      setSurveyors(response.data);
    } catch (error) {
      console.error('Failed to fetch surveyors:', error);
      toast.error('Failed to load surveyors');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/surveyors`, formData);
      toast.success('Surveyor created successfully');
      setDialogOpen(false);
      setFormData({ 
        name: '', 
        type: 'internal', 
        department: 'Engine',
        grades: [],
        mmd_locations: [],
        is_active: true,
        inactive_reason: '',
        transferred_to_mmd: '',
        inactive_date: ''
      });
      fetchSurveyors();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Failed to create surveyor:', error);
      toast.error('Failed to create surveyor');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/surveyors/${currentSurveyor.id}`, formData);
      toast.success('Surveyor updated successfully');
      setDialogOpen(false);
      setEditMode(false);
      setCurrentSurveyor(null);
      setFormData({ 
        name: '', 
        type: 'internal', 
        department: 'Engine',
        grades: [],
        mmd_locations: [],
        is_active: true,
        inactive_reason: '',
        transferred_to_mmd: '',
        inactive_date: ''
      });
      fetchSurveyors();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Failed to update surveyor:', error);
      toast.error('Failed to update surveyor');
    }
  };

  // Open delete confirmation dialog with preview
  const openDeleteDialog = async (surveyor) => {
    setSurveyorToDelete(surveyor);
    setDeleteLoading(true);
    setDeleteDialogOpen(true);
    
    try {
      const response = await axios.get(`${API}/admin/${surveyor.id}/delete-preview`);
      setDeletePreview(response.data);
    } catch (error) {
      console.error('Failed to fetch delete preview:', error);
      toast.error('Failed to load deletion preview');
      setDeleteDialogOpen(false);
    } finally {
      setDeleteLoading(false);
    }
  };

  // Execute the deletion
  const handleDelete = async () => {
    if (!surveyorToDelete || !deletePreview?.can_delete) return;
    
    setDeleteLoading(true);
    try {
      const response = await axios.delete(`${API}/admin/${surveyorToDelete.id}`);
      toast.success(
        `${response.data.message}. ${response.data.questions_reassigned} question(s) reassigned.`
      );
      setDeleteDialogOpen(false);
      setSurveyorToDelete(null);
      setDeletePreview(null);
      fetchSurveyors();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Failed to delete surveyor:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to delete surveyor';
      toast.error(errorMessage);
    } finally {
      setDeleteLoading(false);
    }
  };
  
  // Close delete dialog and reset state
  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSurveyorToDelete(null);
    setDeletePreview(null);
  };

  const openCreateDialog = () => {
    setEditMode(false);
    setCurrentSurveyor(null);
    setFormData({ 
      name: '', 
      type: 'internal', 
      department: 'Engine',
      grades: [],
      mmd_locations: [],
      is_active: true,
      inactive_reason: '',
      transferred_to_mmd: '',
      inactive_date: ''
    });
    setDialogOpen(true);
  };

  const openEditDialog = (surveyor) => {
    setEditMode(true);
    setCurrentSurveyor(surveyor);
    setFormData({ 
      name: surveyor.name, 
      type: surveyor.type,
      department: surveyor.department || 'Engine',
      grades: surveyor.grades || [],
      mmd_locations: surveyor.mmd_locations || [],
      is_active: surveyor.is_active !== undefined ? surveyor.is_active : true,
      inactive_reason: surveyor.inactive_reason || '',
      transferred_to_mmd: surveyor.transferred_to_mmd || '',
      inactive_date: surveyor.inactive_date || ''
    });
    setDialogOpen(true);
  };

  const handleGradeToggle = (grade) => {
    setFormData(prev => ({
      ...prev,
      grades: prev.grades.includes(grade)
        ? prev.grades.filter(g => g !== grade)
        : [...prev.grades, grade]
    }));
  };

  const handleMMDToggle = (mmd) => {
    setFormData(prev => ({
      ...prev,
      mmd_locations: prev.mmd_locations.includes(mmd)
        ? prev.mmd_locations.filter(m => m !== mmd)
        : [...prev.mmd_locations, mmd]
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="spinner"></div>
      </div>
    );
  }

  const activeSurveyors = surveyors.filter(s => s.is_active !== false);
  const inactiveSurveyors = surveyors.filter(s => s.is_active === false);
  
  const activeInternal = activeSurveyors.filter(s => s.type === 'internal');
  const activeExternal = activeSurveyors.filter(s => s.type === 'external');
  const inactiveInternal = inactiveSurveyors.filter(s => s.type === 'internal');
  const inactiveExternal = inactiveSurveyors.filter(s => s.type === 'external');
  
  const getSurveyorDisplayName = (surveyor) => {
    if (!surveyor.is_active || surveyor.is_active === false) {
      if (surveyor.inactive_reason === 'transferred' && surveyor.transferred_to_mmd) {
        return `${surveyor.name} (Transferred to ${surveyor.transferred_to_mmd})`;
      } else if (surveyor.inactive_reason === 'retired') {
        const year = surveyor.inactive_date ? new Date(surveyor.inactive_date).getFullYear() : '';
        return `${surveyor.name} (Retired${year ? ` - ${year}` : ''})`;
      } else {
        return `${surveyor.name} (Inactive)`;
      }
    }
    return surveyor.name;
  };
  
  const renderSurveyorTable = (surveyorsList, showStatus = false) => (
    <div className="border rounded-lg">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Department</TableHead>
            <TableHead>Grades</TableHead>
            <TableHead>MMD Locations</TableHead>
            {showStatus && <TableHead>Status</TableHead>}
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {surveyorsList.length === 0 ? (
            <TableRow>
              <TableCell colSpan={showStatus ? 6 : 5} className="text-center text-gray-500 py-8">
                No surveyors found
              </TableCell>
            </TableRow>
          ) : (
            surveyorsList.map((surveyor) => (
              <TableRow key={surveyor.id}>
                <TableCell className="font-medium">
                  {getSurveyorDisplayName(surveyor)}
                </TableCell>
                <TableCell>
                  <Badge className="bg-[#0A7EA4]">{surveyor.department}</Badge>
                </TableCell>
                <TableCell className="text-sm">
                  {surveyor.grades && surveyor.grades.length > 0 ? surveyor.grades.join(', ') : 'N/A'}
                </TableCell>
                <TableCell className="text-sm">
                  {surveyor.mmd_locations && surveyor.mmd_locations.length > 0 ? surveyor.mmd_locations.join(', ') : 'N/A'}
                </TableCell>
                {showStatus && (
                  <TableCell>
                    {surveyor.is_active !== false ? (
                      <Badge className="bg-green-100 text-green-800 border-green-300">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Active
                      </Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-800 border-amber-300">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        Inactive
                      </Badge>
                    )}
                  </TableCell>
                )}
                <TableCell>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEditDialog(surveyor)}
                      data-testid={`edit-surveyor-${surveyor.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    {surveyor.is_system ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-gray-400 cursor-not-allowed"
                        disabled
                        title="System surveyors cannot be deleted"
                        data-testid={`delete-surveyor-${surveyor.id}-disabled`}
                      >
                        <Shield className="w-4 h-4" />
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => openDeleteDialog(surveyor)}
                        data-testid={`delete-surveyor-${surveyor.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-semibold">Surveyor Management</h3>
            <p className="text-sm text-gray-600">Manage internal and external surveyors</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreateDialog} className="bg-[#0A7EA4] hover:bg-[#0A7EA4]/90">
                <PlusCircle className="w-4 h-4 mr-2" />
                Add Surveyor
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editMode ? 'Edit Surveyor' : 'Add New Surveyor'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={editMode ? handleUpdate : handleCreate} className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="name">Surveyor Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Capt. John Doe"
                    required
                    className="mt-1"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="type">Type *</Label>
                    <Select
                      value={formData.type}
                      onValueChange={(value) => setFormData({ ...formData, type: value })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="internal">Internal</SelectItem>
                        <SelectItem value="external">External</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="department">Department *</Label>
                    <Select
                      value={formData.department}
                      onValueChange={(value) => setFormData({ ...formData, department: value, grades: [] })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Engine">Engine</SelectItem>
                        <SelectItem value="Deck">Deck</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label>Grades *</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2 p-3 border rounded-lg">
                    {availableGrades.map((grade) => (
                      <div key={grade} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`grade-${grade}`}
                          checked={formData.grades.includes(grade)}
                          onChange={() => handleGradeToggle(grade)}
                          className="rounded border-gray-300"
                        />
                        <label htmlFor={`grade-${grade}`} className="text-sm cursor-pointer">
                          {grade}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <Label>MMD Locations *</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2 p-3 border rounded-lg max-h-40 overflow-y-auto">
                    {mmdLocations.map((mmd) => (
                      <div key={mmd} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`mmd-${mmd}`}
                          checked={formData.mmd_locations.includes(mmd)}
                          onChange={() => handleMMDToggle(mmd)}
                          className="rounded border-gray-300"
                        />
                        <label htmlFor={`mmd-${mmd}`} className="text-sm cursor-pointer">
                          {mmd}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Active/Inactive Status (Only in Edit Mode) */}
                {editMode && (
                  <div className="border-t pt-4 space-y-4">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="is_active"
                        checked={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                        className="rounded border-gray-300"
                      />
                      <label htmlFor="is_active" className="text-sm font-medium cursor-pointer">
                        Active Surveyor
                      </label>
                    </div>

                    {!formData.is_active && (
                      <div className="space-y-3 bg-amber-50 p-3 rounded-lg border border-amber-200">
                        <div>
                          <Label htmlFor="inactive_reason">Reason for Inactive Status</Label>
                          <Select
                            value={formData.inactive_reason}
                            onValueChange={(value) => setFormData({ ...formData, inactive_reason: value })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select reason" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="transferred">Transferred</SelectItem>
                              <SelectItem value="retired">Retired</SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {formData.inactive_reason === 'transferred' && (
                          <div>
                            <Label htmlFor="transferred_to_mmd">Transferred To MMD</Label>
                            <Select
                              value={formData.transferred_to_mmd}
                              onValueChange={(value) => setFormData({ ...formData, transferred_to_mmd: value })}
                            >
                              <SelectTrigger className="mt-1">
                                <SelectValue placeholder="Select MMD" />
                              </SelectTrigger>
                              <SelectContent>
                                {mmdLocations.map((mmd) => (
                                  <SelectItem key={mmd} value={mmd}>{mmd}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}

                        <div>
                          <Label htmlFor="inactive_date">Inactive Since</Label>
                          <div className="mt-1">
                            <FilterDatePicker
                              date={formData.inactive_date ? new Date(formData.inactive_date) : null}
                              onDateChange={(date) => setFormData({ ...formData, inactive_date: date ? date.toISOString() : '' })}
                              placeholder="Select inactive date"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 bg-[#14B8A6] hover:bg-[#14B8A6]/90">
                    {editMode ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="space-y-8">
          {/* Active Surveyors */}
          <div className="space-y-6">
            <h3 className="text-xl font-bold flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Active Surveyors ({activeSurveyors.length})
            </h3>
            
            {/* Active Internal Surveyors */}
            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Badge className="bg-[#0A7EA4]">Internal</Badge>
                ({activeInternal.length})
              </h4>
              {renderSurveyorTable(activeInternal, false)}
            </div>

            {/* Active External Surveyors */}
            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Badge className="bg-[#14B8A6]">External</Badge>
                ({activeExternal.length})
              </h4>
              {renderSurveyorTable(activeExternal, false)}
            </div>
          </div>

          {/* Inactive Surveyors */}
          {inactiveSurveyors.length > 0 && (
            <div className="space-y-6 pt-6 border-t">
              <h3 className="text-xl font-bold flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                Past Surveyors ({inactiveSurveyors.length})
              </h3>
              
              {/* Inactive Internal Surveyors */}
              {inactiveInternal.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Badge className="bg-[#0A7EA4]">Internal</Badge>
                    ({inactiveInternal.length})
                  </h4>
                  {renderSurveyorTable(inactiveInternal, true)}
                </div>
              )}

              {/* Inactive External Surveyors */}
              {inactiveExternal.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Badge className="bg-[#14B8A6]">External</Badge>
                    ({inactiveExternal.length})
                  </h4>
                  {renderSurveyorTable(inactiveExternal, true)}
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={closeDeleteDialog}>
          <DialogContent className="max-w-md" data-testid="delete-surveyor-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {deletePreview?.can_delete === false ? (
                  <>
                    <Shield className="w-5 h-5 text-blue-600" />
                    Cannot Delete Surveyor
                  </>
                ) : (
                  <>
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                    Confirm Deletion
                  </>
                )}
              </DialogTitle>
            </DialogHeader>
            
            {deleteLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                <span className="ml-2 text-gray-600">Loading...</span>
              </div>
            ) : deletePreview ? (
              <div className="space-y-4">
                {/* Surveyor Info */}
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="font-medium">{deletePreview.surveyor?.name}</p>
                  <p className="text-sm text-gray-600">
                    Type: <Badge variant="outline" className="ml-1">{deletePreview.surveyor?.type}</Badge>
                  </p>
                </div>
                
                {deletePreview.can_delete === false ? (
                  <>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-blue-800 text-sm">
                        <Shield className="w-4 h-4 inline mr-1" />
                        {deletePreview.reason || "System surveyors are protected and cannot be deleted."}
                      </p>
                      <p className="text-blue-700 text-xs mt-2">
                        These surveyors are essential for the platform to function. Questions without an assigned surveyor are automatically linked to them.
                      </p>
                    </div>
                    <DialogFooter>
                      <Button onClick={closeDeleteDialog} className="w-full" data-testid="close-delete-dialog">
                        Understood
                      </Button>
                    </DialogFooter>
                  </>
                ) : (
                  <>
                    {/* Impact Warning */}
                    {deletePreview.impact?.total_questions_affected > 0 && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <p className="text-amber-800 font-medium text-sm">
                          ⚠️ This will affect {deletePreview.impact.total_questions_affected} question(s)
                        </p>
                        <ul className="text-amber-700 text-xs mt-2 space-y-1">
                          {deletePreview.impact.as_internal_surveyor > 0 && (
                            <li>• {deletePreview.impact.as_internal_surveyor} as internal surveyor</li>
                          )}
                          {deletePreview.impact.as_external_surveyor > 0 && (
                            <li>• {deletePreview.impact.as_external_surveyor} as external surveyor</li>
                          )}
                        </ul>
                      </div>
                    )}
                    
                    {/* Reassignment Info */}
                    <div className="bg-gray-100 rounded-lg p-4">
                      <p className="text-sm text-gray-700">
                        Questions will be reassigned to: <strong>{deletePreview.reassignment?.replacement_name}</strong>
                      </p>
                    </div>
                    
                    <DialogDescription className="text-sm text-gray-500">
                      This action cannot be undone. The surveyor will be permanently removed and all their questions will be reassigned.
                    </DialogDescription>
                    
                    <DialogFooter className="flex gap-2 sm:gap-0">
                      <Button 
                        variant="outline" 
                        onClick={closeDeleteDialog}
                        disabled={deleteLoading}
                        data-testid="cancel-delete"
                      >
                        Cancel
                      </Button>
                      <Button 
                        variant="destructive"
                        onClick={handleDelete}
                        disabled={deleteLoading}
                        className="bg-red-600 hover:bg-red-700"
                        data-testid="confirm-delete-surveyor"
                      >
                        {deleteLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Deleting...
                          </>
                        ) : (
                          <>
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete Surveyor
                          </>
                        )}
                      </Button>
                    </DialogFooter>
                  </>
                )}
              </div>
            ) : null}
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default SurveyorManagement;